/**
 * TTS Utility Functions
 * Shared utilities for text-to-speech sentence processing
 */

import { TTS_CONFIG } from "../entities";

/**
 * Splits text into sentences using intelligent boundary detection.
 * Handles edge cases like:
 * - List markers (1., 2., etc.)
 * - Decimal numbers (0.01, 3.14)
 * - Multiple sentence terminators
 *
 * @param text - The text to split
 * @returns Array of trimmed, non-empty sentences
 *
 * @example
 * splitIntoSentences("Hello. World. 1. Call 911.")
 * // => ["Hello.", "World.", "1. Call 911."]
 */
export function splitIntoSentences(text: string): string[] {
    if (!text) return []
    return (text.match(TTS_CONFIG.SENTENCE_SPLIT_REGEX) ?? [])
        .map((s) => s.trim())
        .filter(Boolean)
}

/**
 * Calculates character ranges for each sentence in the original text.
 * Useful for mapping rendered content back to source positions.
 *
 * @param content - The full text content
 * @returns Array of {start, end} objects representing character ranges
 *
 * @example
 * calculateSentenceRanges("Hello world. Good morning.")
 * // => [
 * //   { start: 0, end: 12 },
 * //   { start: 13, end: 28 }
 * // ]
 */
export function calculateSentenceRanges(
    content: string
): Array<{ start: number; end: number }> {
    const sentences = splitIntoSentences(content)
    const ranges: Array<{ start: number; end: number }> = []
    let currentPos = 0

    for (const sentence of sentences) {
        const start = content.indexOf(sentence, currentPos)
        if (start === -1) continue
        const end = start + sentence.length
        ranges.push({ start, end })
        currentPos = end
    }

    return ranges
}

/**
 * Recursively extracts text content from React nodes.
 * Handles strings, numbers, React elements, and arrays.
 *
 * @param node - React node (can be string, element, array, etc.)
 * @returns Concatenated text content
 *
 * @example
 * extractText("Hello")                           // => "Hello"
 * extractText(42)                                // => "42"
 * extractText(<span>World</span>)                // => "World"
 * extractText([<span>Hello</span>, " World"])    // => "Hello World"
 */
export function extractText(node: React.ReactNode): string {
    if (typeof node === 'string') return node
    if (typeof node === 'number') return String(node)

    if (require('react').isValidElement(node)) {
        return extractText((node as React.ReactElement).props.children)
    }

    if (Array.isArray(node)) {
        return node.map(extractText).join('')
    }

    return ''
}

/**
 * Finds a speech synthesis voice matching the given language code.
 * Tries exact match first, then prefix match (e.g., 'en' for 'en-US'),
 * then returns first available voice as fallback.
 *
 * @param voices - Available voices from Web Speech API
 * @param language - Language code to match (e.g., 'en-US', 'es')
 * @returns Matching voice if found, first available voice, or undefined
 *
 * @example
 * const voice = findVoiceByLanguage(window.speechSynthesis.getVoices(), 'es-MX')
 */
export function findVoiceByLanguage(
    voices: SpeechSynthesisVoice[] | null,
    language: string
): SpeechSynthesisVoice | undefined {
    if (!voices || voices.length === 0) return undefined

    // Try exact match first
    const exactMatch = voices.find(
        (v) => v.lang.toLowerCase() === language.toLowerCase()
    )
    if (exactMatch) return exactMatch

    // Try language prefix match (e.g., 'en' for 'en-US')
    const languagePrefix = language.split('-')[0].toLowerCase()
    const prefixMatch = voices.find((v) =>
        v.lang.toLowerCase().startsWith(languagePrefix)
    )
    if (prefixMatch) return prefixMatch

    // Return first available voice as fallback
    return voices[0]
}
